import { BehaviorSubject, ReplaySubject, Subject, combineLatest, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { Component } from '@angular/core';

import { MobileReputationService, MobileThemeService } from '@sifca-monorepo/clients';
import { DayEnum, LoyaltySettingsAccelerateSlotType, LoyaltySettingsType } from '@sifca-monorepo/widget-generator';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';

type HappyHourChannel = 'mobile' | 'physical' | 'web';

interface HappyHourSlotViewModel {
  id: string;
  ratio: number;
  day?: DayEnum | null;
  from?: string | null;
  to?: string | null;
}

interface HappyHourTab {
  id: HappyHourChannel;
  labelKey: string;
  isActive: boolean;
}

interface HappyHourHero {
  title?: string;
  imageUrl?: string | null;
}

interface HappyHourViewModel {
  hero: HappyHourHero | null;
  tabs: HappyHourTab[];
  selectedChannel: HappyHourChannel | null;
  slots: HappyHourSlotViewModel[];
  showEmptyState: boolean;
}

const HAPPY_HOUR_CHANNEL_LABEL: Record<HappyHourChannel, string> = {
  mobile: 'HAPPY_HOUR_TAB_MOBILE',
  physical: 'HAPPY_HOUR_TAB_ONSITE',
  web: 'HAPPY_HOUR_TAB_WEB',
};

const DAY_TRANSLATION_KEYS: Record<DayEnum, string> = {
  [DayEnum.MONDAY]: 'DAY_MONDAY',
  [DayEnum.TUESDAY]: 'DAY_TUESDAY',
  [DayEnum.WEDNESDAY]: 'DAY_WEDNESDAY',
  [DayEnum.THURSDAY]: 'DAY_THURSDAY',
  [DayEnum.FRIDAY]: 'DAY_FRIDAY',
  [DayEnum.SATURDAY]: 'DAY_SATURDAY',
  [DayEnum.SUNDAY]: 'DAY_SUNDAY',
};

@Component({
  template: '',
})
export class MobileHappyHourComponent {
  private unsubscribeAll = new Subject<void>();
  private selectedChannel$ = new BehaviorSubject<HappyHourChannel | null>(null);
  private selectedLoyalty$ = new ReplaySubject<LoyaltySettingsType | null>(1);

  isIos: boolean;
  slotsIdentical = false;
  dark$ = this.themeService.darkMode$;

  vm$ = combineLatest([this.selectedLoyalty$, this.selectedChannel$]).pipe(
    map(([settings, channel]) => this.buildViewModel(settings, channel)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    protected router: Router,
    protected themeService: MobileThemeService,
    protected reputationService: MobileReputationService,
    private mobPlatform: Platform,
  ) {
    this.isIos = this.mobPlatform.is('ios');
    const nav = this.router.getCurrentNavigation();
    const selectedPartner = nav?.extras?.state?.partner;
    this.reputationService
      .findLoyaltySettingsByTargetOrAggregator(selectedPartner?.pos?.id)
      .pipe(
        switchMap((res) => {
          this.selectedLoyalty$.next(res);
          this.slotsIdentical = this.areAllChannelSlotsIdentical(res);
          this.updateSelectedChannel(res);
          return of(res);
        }),
      )
      .subscribe();
  }

  areAllChannelSlotsIdentical(settings: LoyaltySettingsType | null): boolean {
    if (!settings?.accelerate) {
      return true;
    }
    const mobileSlots = settings.accelerate.mobile?.slots ?? [];
    const physicalSlots = settings.accelerate.physical?.slots ?? [];
    const webSlots = settings.accelerate.web?.slots ?? [];
    const slotsEqual = (a: unknown[], b: unknown[]) => JSON.stringify(a) === JSON.stringify(b);
    return slotsEqual(mobileSlots, physicalSlots) && slotsEqual(mobileSlots, webSlots);
  }

  selectChannel(channel: HappyHourChannel): void {
    if (this.selectedChannel$.value !== channel) {
      this.selectedChannel$.next(channel);
    }
  }

  trackSlotById(_index: number, slot: HappyHourSlotViewModel): string {
    return slot.id;
  }

  private buildViewModel(settings: LoyaltySettingsType | null, selectedChannel: HappyHourChannel | null): HappyHourViewModel {
    if (!settings) {
      return {
        hero: null,
        tabs: [],
        selectedChannel: null,
        slots: [],
        showEmptyState: false,
      };
    }
    const availableChannels = this.getAvailableChannels(settings);
    const forceMobileOnly = this.slotsIdentical && availableChannels.includes('mobile');
    let resolvedChannel = selectedChannel && availableChannels.includes(selectedChannel) ? selectedChannel : (availableChannels[0] ?? null);

    if (forceMobileOnly) {
      resolvedChannel = 'mobile';
    }

    if (resolvedChannel && resolvedChannel !== this.selectedChannel$.value) {
      this.selectedChannel$.next(resolvedChannel);
    }

    const slotViewModels = resolvedChannel ? this.buildSlotViewModels(settings, resolvedChannel) : [];

    return {
      hero: this.buildHero(settings),
      tabs: forceMobileOnly
        ? []
        : availableChannels.map((channel) => ({
            id: channel,
            labelKey: HAPPY_HOUR_CHANNEL_LABEL[channel],
            isActive: channel === resolvedChannel,
          })),
      selectedChannel: resolvedChannel,
      slots: slotViewModels,
      showEmptyState: !slotViewModels.length,
    };
  }

  private buildHero(settings: LoyaltySettingsType | null): HappyHourHero | null {
    if (!settings?.target?.pos) {
      return null;
    }

    const picture = settings.target.pos.picture;
    return {
      title: settings.target.pos.title ?? '',
      imageUrl: picture?.baseUrl && picture?.path ? `${picture.baseUrl}/${picture.path}` : null,
    };
  }

  private updateSelectedChannel(settings: LoyaltySettingsType | null): void {
    const availableChannels = this.getAvailableChannels(settings);
    if (!availableChannels.length) {
      if (this.selectedChannel$.value !== null) {
        this.selectedChannel$.next(null);
      }
      return;
    }

    if (!this.selectedChannel$.value || !availableChannels.includes(this.selectedChannel$.value)) {
      this.selectedChannel$.next(availableChannels[0]);
    }
  }

  private getAvailableChannels(settings: LoyaltySettingsType | null): HappyHourChannel[] {
    if (!settings?.accelerate) {
      return [];
    }

    const order: HappyHourChannel[] = ['mobile', 'physical', 'web'];
    return order.filter((channel) => {
      const platform = this.getAcceleratePlatform(settings, channel);
      return Boolean(platform?.enabled) && Boolean(platform?.slots?.length);
    });
  }

  private getAcceleratePlatform(settings: LoyaltySettingsType | null, channel: HappyHourChannel | null) {
    if (!settings?.accelerate || !channel) {
      return null;
    }

    switch (channel) {
      case 'mobile':
        return settings.accelerate.mobile ?? null;
      case 'physical':
        return settings.accelerate.physical ?? null;
      case 'web':
        return settings.accelerate.web ?? null;
      default:
        return null;
    }
  }

  private getSlotsForChannel(settings: LoyaltySettingsType | null, channel: HappyHourChannel | null): LoyaltySettingsAccelerateSlotType[] {
    const platform = this.getAcceleratePlatform(settings, channel);
    return platform?.slots ?? [];
  }

  private buildSlotViewModels(settings: LoyaltySettingsType, channel: HappyHourChannel): HappyHourSlotViewModel[] {
    const slots = this.getSlotsForChannel(settings, channel);
    return slots.map((slot, index) => this.mapSlot(slot, index, channel)).filter((slot): slot is HappyHourSlotViewModel => Boolean(slot));
  }

  private mapSlot(slot: LoyaltySettingsAccelerateSlotType, index: number, channel: HappyHourChannel): HappyHourSlotViewModel | null {
    if (!slot?.period) {
      return null;
    }

    const ratio = slot.ratio ?? 1;
    return {
      id: `${channel}-${slot.period.day}-${slot.period.from}-${slot.period.to}-${index}`,
      ratio,
      day: slot.period.day,
      from: slot.period.from,
      to: slot.period.to,
    };
  }

  protected getDayTranslationKey(day?: DayEnum | null): string {
    return day ? (DAY_TRANSLATION_KEYS[day] ?? day) : '';
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
    this.selectedChannel$.complete();
  }
}
